// /admin/activity-scoring — admin panel for tuning per-activity scores.
//
// Reads activity_scoring_config. Admin toggles `enabled` and edits `score`
// per activity type; clicking Save PATCHes each row that changed. Trigger-
// written events already use the current config row (score is snapshotted
// per-event, so edits affect future events without rewriting history).

const _isRowDirty = (r) => r._draftEnabled !== r.enabled || r._draftScore !== r.score
const _patchRow = (rows, type, patch) => rows.map(r => r.activity_type === type ? { ...r, ...patch } : r)
const _commitDraft = (root, rows) => root?.update({
  activityScoring: rows,
  activityScoringDirty: rows.some(_isRowDirty)
})

export const adminActivityScoring = {
  extends: 'AppPage',
  metadata: { title: 'Activity scoring / Admin / Symbols Workspace' },

  onInit: (el, s) => {
    if (!el.call('shouldRedirectNoPerm', s, 'admin.pages.read')) return
    const root = el.call('findRouteRoot', el)
    if (root) el.router('/', root, {}, { pushState: true })
  },

  fetch: {
    // migrated 2026-04-27 to entity route — workspaceProject.activity has a
    // dedicated `scoringConfig` op that returns the activity_scoring_config
    // rows. method: 'select' maps to the `list` op for that route.
    method: 'select',
    from: 'workspaceProject.activity.scoringConfig',
    as: '~/activityScoring',
    transform: (rows) => ({
      activityScoring: (rows || []).map((r) => ({
        activity_type: r.activity_type,
        enabled: r.enabled,
        score: r.score,
        label: r.label,
        description: r.description || '',
        // Draft values — edits flow here; Save writes them back and clears
        _draftEnabled: r.enabled,
        _draftScore: r.score
      })),
      activityScoringDirty: false
    })
  },

  AdminSidebar: {},

  AdminMain: {
    extends: 'AppMain',
    padding: '0',

    Header: {
      extends: 'PageHeader',
      LeftGroup: { Title: { text: 'Activity Scoring' } }
    },

    Body: {
      overflow: 'auto',
      padding: 'B',
      flow: 'y',
      gap: 'A',

      Intro: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z2',
        color: 'caption',
        lineHeight: '1.5',
        maxWidth: 'L',
        text: 'Each event below is scored when the underlying action happens (standup saved, ticket moved, note edited, KB doc updated, call analyzed). Scores are snapshot-per-event — editing a score changes future events only, never rewrites history. Disabling an activity stops emitting new events of that type.'
      },

      Card: {
        flow: 'y',
        round: 'Z2',
        background: 'overlay',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'line',
        padding: '0',
        overflow: 'hidden',

        HeaderRow: {
          flow: 'x',
          align: 'center',
          padding: 'Z1 A',
          borderBottomWidth: '2px',
          borderBottomStyle: 'solid',
          borderBottomColor: 'line',
          childProps: {
            fontSize: 'Y1',
            fontWeight: '700',
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            color: 'caption'
          },
          H01_Activity: { flex: '2', text: 'Activity' },
          H02_Description: { flex: '3', text: 'Description' },
          H03_Enabled: { flex: '1', textAlign: 'center', text: 'Enabled' },
          H04_Score: { flex: '1', textAlign: 'right', text: 'Score (pts)' }
        },

        Rows: {
          flow: 'y',
          childExtends: 'AdminActivityScoringRow',
          childrenAs: 'state',
          children: (el, s) => s.root?.activityScoring || []
        }
      },

      FooterRow: {
        flow: 'x',
        align: 'center space-between',
        gap: 'A',

        Status: {
          tag: 'span',
          fontSize: 'Y2',
          color: (el, s) => s.root?.activityScoringDirty ? 'yellow' : 'caption',
          text: (el, s) => {
            if (s.root?._activityScoringSaving) return 'Saving…'
            if (s.root?._activityScoringSaved) return 'Saved.'
            if (s.root?.activityScoringDirty) return 'Unsaved changes'
            return 'All changes saved.'
          }
        },

        Actions: {
          flow: 'x',
          gap: 'Z',

          ResetBtn: {
            hide: (el, s) => !s.root?.activityScoringDirty,
            tag: 'button',
            type: 'button',
            text: 'Reset',
            padding: 'Y A',
            round: 'Y',
            borderWidth: '1px',
            borderColor: 'line',
            borderStyle: 'solid',
            background: 'transparent',
            color: 'title',
            cursor: 'pointer',
            fontSize: 'Z2',
            onClick: (e, el, s) => {
              const reset = (s.root?.activityScoring || []).map((r) => ({
                ...r,
                _draftEnabled: r.enabled,
                _draftScore: r.score
              }))
              s.root?.update({
                activityScoring: reset,
                activityScoringDirty: false
              })
            }
          },

          SaveBtn: {
            tag: 'button',
            type: 'button',
            text: 'Save changes',
            padding: 'Y A1',
            round: 'Y',
            border: 'none',
            theme: 'primary',
            cursor: 'pointer',
            fontSize: 'Z2',
            fontWeight: '600',
            opacity: (el, s) => s.root?.activityScoringDirty ? '1' : '.5',
            pointerEvents: (el, s) => s.root?.activityScoringDirty ? 'auto' : 'none',
            onClick: async (e, el, s) => {
              const rows = s.root?.activityScoring || []
              const dirty = rows.filter(_isRowDirty)
              if (!dirty.length) return
              s.root?.update({ _activityScoringSaving: true })
              const ok = await el.call('updateActivityScoring', dirty)
              if (!ok) {
                s.root?.update({ _activityScoringSaving: false })
                return
              }
              const dirtyByType = new Map(dirty.map(d => [d.activity_type, d]))
              s.root?.update({
                activityScoring: rows.map(r => {
                  const d = dirtyByType.get(r.activity_type)
                  return d ? { ...r, enabled: d._draftEnabled, score: d._draftScore } : r
                }),
                activityScoringDirty: false,
                _activityScoringSaving: false,
                _activityScoringSaved: true
              })
              clearTimeout(el._savedTimer)
              el._savedTimer = setTimeout(
                () => s.root?.update({ _activityScoringSaved: false }),
                2000
              )
            },
            onRemove: (el) => clearTimeout(el._savedTimer)
          }
        }
      }
    }
  }
}

// ── Row ─────────────────────────────────────────────────────────────
export const AdminActivityScoringRow = {
  flow: 'x',
  align: 'center',
  padding: 'Z1 A',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'line',
  opacity: (el, s) => s._draftEnabled ? '1' : '.5',

  Activity: {
    flex: '2',
    flow: 'y',
    gap: 'V',

    Label: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => s.label || s.activity_type || ''
    },
    Slug: {
      tag: 'code',
      fontSize: 'Y1',
      color: 'caption',
      fontFamily: 'mono',
      text: (el, s) => s.activity_type || ''
    }
  },

  Description: {
    flex: '3',
    padding: '0 A 0 0',
    fontSize: 'Y2',
    color: 'caption',
    lineHeight: '1.4',
    text: (el, s) => s.description || ''
  },

  EnabledCell: {
    flex: '1',
    align: 'center center',

    Toggle: {
      Input: {
        checked: (el, s) => !!s._draftEnabled,
        ':checked + div': {
          background: 'green',
          justifyContent: 'flex-end'
        },
        onChange: (e, el, s) => {
          const rows = _patchRow(s.root?.activityScoring || [], s.activity_type, { _draftEnabled: !s._draftEnabled })
          _commitDraft(s.root, rows)
        }
      }
    }
  },

  ScoreCell: {
    flex: '1',
    align: 'center right',

    Input: {
      tag: 'input',
      type: 'number',
      min: '0',
      max: '100',
      step: '1',
      width: 'C1',
      padding: 'Y Z',
      round: 'Y',
      borderWidth: '1px',
      borderColor: 'line',
      borderStyle: 'solid',
      background: 'overlayStrong',
      color: 'title',
      fontSize: 'Z2',
      textAlign: 'right',
      value: (el, s) => String(s._draftScore ?? s.score ?? 0),
      onCreate: (el, s) => {
        el.node.value = String(s._draftScore ?? s.score ?? 0)
      },
      onInput: (e, el, s) => {
        const n = parseInt(el.node.value, 10)
        const next = Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0
        const rows = _patchRow(s.root?.activityScoring || [], s.activity_type, { _draftScore: next })
        _commitDraft(s.root, rows)
      }
    }
  }
}
